import * as React from "react";
import {
  List,
  ListItem,
  ListItemText,
  Typography,
  FormControl,
  Stack,
  InputAdornment,
  OutlinedInput,
  Box,
  Checkbox,
  ListItemIcon,
  ListItemButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { findProductType } from "../../utils";
import { Loader } from "./Loader";
import { LoadingButton } from "./LoadingButton";

export const CategoryProductDialog = (props) => {
  const {
    Items,
    onClearSearch,
    onSearch,
    onSelect,
    disabled,
    onSubmit,
    selectedItems,
    existedProductList,
    buttonText = "Save",
    type = "add",
    isLoading = false,
    isButtonLoading = false,
    hasVariantLevel = false,
  } = props;
  const [value, setValue] = React.useState("");

  const lengthOfTotal = React.useMemo(() => {
    if (type !== "modify") return 0;
    return Items.reduce((acc, item) => {
      return (
        acc +
        (item.products || []).reduce((pAcc, product) => {
          if (
            hasVariantLevel &&
            product.has_variant &&
            product.variants?.length
          ) {
            return pAcc + product.variants.length;
          }
          return pAcc + 1;
        }, 0)
      );
    }, 0);
  }, [Items, type, hasVariantLevel]);

  const isCheckAllSelected = React.useCallback(() => {
    if (type !== "modify") return false;
    return lengthOfTotal > 0 && selectedItems.length === lengthOfTotal;
  }, [lengthOfTotal, selectedItems, type]);

  const isCheckIndeterminate = React.useCallback(() => {
    if (type !== "modify") return false;
    return (
      lengthOfTotal > 0 &&
      selectedItems.length > 0 &&
      selectedItems.length < lengthOfTotal
    );
  }, [lengthOfTotal, selectedItems, type]);

  return (
    <>
      <Stack
        justifyContent="space-between"
        height="100%"
        gap="8px"
        maxHeight={"calc(100% - 24px)"}
      >
        <FormControl
          size="small"
          fullWidth={true}
          sx={{
            pt: "24px",
          }}
        >
          <OutlinedInput
            placeholder="Search"
            sx={{
              height: "48px",
              ".MuiOutlinedInput-input": {
                pl: "0px !important",
              },
            }}
            size="small"
            value={value}
            startAdornment={
              <InputAdornment position="start">
                <SearchIcon size="large" />
              </InputAdornment>
            }
            endAdornment={
              value &&
              onSearch && (
                <InputAdornment
                  sx={{ cursor: "pointer" }}
                  onClick={() => {
                    setValue("");
                    onSearch("search", "");
                  }}
                  position="end"
                >
                  <CloseIcon size="large" />
                </InputAdornment>
              )
            }
            fullWidth
            onChange={(e) => {
              setValue(e.target.value);
              onSearch("search", e.target.value);
            }}
          />
        </FormControl>
        <Box sx={{ height: "100%", overflow: "auto" }} fullWidth>
          {Items.length > 0 ? (
            <List sx={{ width: "100%", py: 0, bgcolor: "background.paper" }}>
              {type === "modify" && (
                <ListItem key={value} disablePadding>
                  <ListItemButton
                    role={undefined}
                    dense
                    sx={{
                      "&:hover": {
                        backgroundColor: "rgba(21, 96, 212, 0.08)",
                        borderRadius: "8px",
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: "32px",
                      }}
                    >
                      <Checkbox
                        edge="start"
                        checked={isCheckAllSelected()}
                        indeterminate={isCheckIndeterminate()}
                        onChange={(e) => {
                          onSelect("all", Items, e);
                        }}
                        disableRipple
                        inputProps={{ "aria-labelledby": "all" }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      id="all"
                      primary="Select All"
                      sx={{
                        "& .MuiTypography-root": {
                          fontWeight: 600,
                        },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              )}
              {Items.map((value) => {
                const labelId = `checkbox-list-label-${value}`;

                return (
                  <>
                    {value.products.length === 0 ? (
                      ""
                    ) : (
                      <ListOfProduct
                        value={value}
                        labelId={labelId}
                        onSelect={onSelect}
                        selectedItems={selectedItems}
                        alReadyIDs={existedProductList.map((e, i) => {
                          return e.id;
                        })}
                        type={type}
                        hasVariantLevel={hasVariantLevel}
                      />
                    )}
                  </>
                );
              })}
            </List>
          ) : isLoading ? (
            <Loader />
          ) : (
            <Stack justifyContent="center" alignItems="center" height="100%">
              <Typography
                sx={{ display: "inline" }}
                component="span"
                variant="caption"
                color="text.secondary"
                fontWeight={400}
              >
                There is no products found
              </Typography>
            </Stack>
          )}
        </Box>
        <LoadingButton
          sx={{ height: "48px" }}
          variant="contained"
          fullWidth
          loading={isButtonLoading}
          disabled={
            type === "modify" ? disabled : !selectedItems.length || disabled
          }
          onClick={onSubmit}
        >
          {buttonText}
        </LoadingButton>
      </Stack>
    </>
  );
};

function ListOfProduct({
  value,
  labelId,
  onSelect,
  selectedItems,
  alReadyIDs,
  type,
  hasVariantLevel,
}) {
  const [open, setOpen] = React.useState(false);
  const getLeafIds = React.useCallback(
    (products = []) => {
      return products.flatMap((product) => {
        if (hasVariantLevel && product?.has_variant) {
          return (product.variants || []).map((variant) => variant.id);
        }
        return [product.id];
      });
    },
    [hasVariantLevel],
  );

  const isSelectedCheck = (value) => {
    const allIDs = new Set(selectedItems.concat(alReadyIDs));
    const leafIds = getLeafIds(value.products || []);

    return leafIds.length > 0 && leafIds.every((id) => allIDs.has(id));
  };

  const isCheckIndeterminate = (data) => {
    const leafIds = getLeafIds(data.products || []);
    if (!leafIds.length) return false;

    const selectedIds = new Set(selectedItems.concat(alReadyIDs));
    const selectedCount = leafIds.filter((id) => selectedIds.has(id)).length;

    return selectedCount > 0 && selectedCount < leafIds.length;
  };

  const isSelected = (id) => {
    if (alReadyIDs.includes(id)) {
      return true;
    } else {
      return selectedItems.includes(id);
    }
  };

  const isDisabled = (value) => {
    if (type === "modify") return false;
    return alReadyIDs.includes(value);
  };
  // console.log("aaaaa",value.products);

  return (
    <Box
      sx={{
        backgroundColor: open ? "rgba(21, 96, 212, 0.08)" : "",
        borderRadius: "8px",
        mb: 0.5,
      }}
    >
      <ListItem key={value} disablePadding>
        <ListItemButton
          role={undefined}
          dense
          sx={{
            "&:hover": {
              backgroundColor: "rgba(21, 96, 212, 0.08)",
              borderRadius: "8px",
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: "32px",
            }}
          >
            <Checkbox
              edge="start"
              disabled={isDisabled(value)}
              checked={isSelectedCheck(value)}
              indeterminate={isCheckIndeterminate(value)}
              onChange={(e) => {
                onSelect("head", value, e);
                setOpen(true);
              }}
              disableRipple
              inputProps={{ "aria-labelledby": labelId }}
            />
          </ListItemIcon>
          <ListItemText
            id={labelId}
            primary={value.name}
            onClick={() => setOpen(!open)}
            sx={{
              "& .MuiTypography-root": {
                fontWeight: 600,
              },
            }}
          />
          {value.products?.length > 0 && (
            <img
              src="/icons/ic_dropdown.svg"
              style={{
                transform: !open ? "rotate(180deg)" : "",
              }}
              onClick={() => setOpen(!open)}
            />
          )}
        </ListItemButton>
      </ListItem>
      {open &&
        value.products.map((v) => {
          if (hasVariantLevel && v.has_variant) {
            return (
              <Box sx={{ height: "100%", overflow: "auto" }} fullWidth>
                <List sx={{ width: "100%", py: 0 }}>
                  {v.variants.length === 0 ? (
                    ""
                  ) : (
                    <ListOfVarientProduct
                      value={v}
                      labelId={labelId}
                      onSelect={onSelect}
                      selectedItems={selectedItems}
                      alReadyIDs={alReadyIDs}
                      type={type}
                    />
                  )}
                </List>
              </Box>
            );
          } else {
            return (
              <ListItemButton
                role={undefined}
                dense
                sx={{
                  "&:hover": {
                    backgroundColor: "rgba(21, 96, 212, 0.08)",
                    borderRadius: "8px",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: "32px",
                    marginLeft: "12px",
                  }}
                >
                  <Checkbox
                    edge="start"
                    checked={isSelected(v.id)}
                    disabled={isDisabled(v.id)}
                    defaultChecked={true}
                    // tabIndex={-1}
                    onChange={(e) => onSelect("body", v, e)}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText>
                  {" "}
                  {v.name}
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50px",
                      height: "18px",
                      width: "18px",
                      backgroundColor: "#1560D414",
                      color: "#1560D4",
                      fontSize: "10px",
                      marginLeft: "6px",
                    }}
                  >
                    {findProductType(v.type)}
                  </span>
                </ListItemText>
              </ListItemButton>
            );
          }
        })}
    </Box>
  );
}
export default ListOfProduct;

export function ListOfVarientProduct({
  value,
  labelId,
  onSelect,
  selectedItems,
  alReadyIDs,
  type,
  hasVariantLevel,
}) {
  const [open, setOpen] = React.useState(false);
  const isSelectedCheck = (value) => {
    let allIDs = selectedItems.concat(alReadyIDs);
    let aa = value.variants?.map((e, i) => {
      return e.id;
    });
    let a = value.variants
      ?.map((e, i) => {
        return e.id;
      })
      .every((val) => allIDs.includes(val));
    return value.variants
      ?.map((e, i) => {
        return e.id;
      })
      .every((val) => allIDs.includes(val));
  };

  const isCheckIndeterminate = (data) => {
    const subArray = data.variants.map((e, i) => e.id);
    const commonIDs1 = selectedItems.filter((val) => subArray.includes(val));
    const commonIDs2 = alReadyIDs.filter((val) => subArray.includes(val));

    let commonIDs = commonIDs1.concat(commonIDs2);
    const notAllIDs = subArray.every((val) => commonIDs.includes(val));

    return commonIDs.length > 0 && !notAllIDs;
  };

  const isSelected = (id) => {
    if (alReadyIDs.includes(id)) {
      return true;
    } else {
      return selectedItems.includes(id);
    }
  };

  const isDisabled = (value) => {
    if (type === "modify") return false;
    return alReadyIDs.includes(value);
  };

  return (
    <Box
      sx={{
        backgroundColor: open ? "rgba(21, 96, 212, 0.08)" : "",
        borderRadius: "8px",
        mb: 0.5,
        px: "12px",
      }}
    >
      <ListItem key={value} disablePadding>
        <ListItemButton
          role={undefined}
          dense
          sx={{
            "&:hover": {
              // backgroundColor: "rgba(21, 96, 212, 0.08)",
              borderRadius: "8px",
              px: "16px",
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: "32px",
            }}
          >
            <Checkbox
              edge="start"
              disabled={isDisabled(value)}
              checked={isSelectedCheck(value)}
              indeterminate={isCheckIndeterminate(value)}
              onChange={(e) => {
                onSelect("head", value, e);
                setOpen(true);
              }}
              disableRipple
              inputProps={{ "aria-labelledby": labelId }}
            />
          </ListItemIcon>
          <ListItemText
            id={labelId}
            primary={value.name}
            onClick={() => setOpen(!open)}
            sx={{
              "& .MuiTypography-root": {
                fontWeight: 600,
              },
            }}
          />
          {value.variants?.length > 0 && (
            <img
              src="/icons/ic_dropdown.svg"
              style={{
                transform: !open ? "rotate(180deg)" : "",
              }}
              onClick={() => setOpen(!open)}
            />
          )}
        </ListItemButton>
      </ListItem>
      {open &&
        value.variants.map((v) => {
          return (
            <ListItemButton
              role={undefined}
              dense
              sx={{
                "&:hover": {
                  backgroundColor: "rgba(21, 96, 212, 0.08)",
                  borderRadius: "8px",
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: "32px",
                  marginLeft: "12px",
                }}
              >
                <Checkbox
                  edge="start"
                  checked={isSelected(v.id)}
                  disabled={isDisabled(v.id)}
                  defaultChecked={true}
                  // tabIndex={-1}
                  onChange={(e) => onSelect("body", v, e)}
                  disableRipple
                />
              </ListItemIcon>
              <ListItemText>{v.name}</ListItemText>
            </ListItemButton>
          );
        })}
    </Box>
  );
}
